import { Component, createRef } from 'preact'
import { PostType } from './Types'
import { common } from '../common'
import Content from '../common/Content'
import PostEditor from '@insided/shared/uikit/PostEditor'
import PostAuthor from './PostAuthorAvatar'
import PostAuthorMeta from './PostAuthorMeta'
import Axios from 'axios'
import LikedUsers, { LikedUserType } from '../topic-list-view/LikedUsers'
import Phrase from '../../libs/translationsUtils/phrase'
import classNames from 'classnames'
import PinnedReplyLabel from '../pinned-reply/PinnedReplyLabel'
import { canUserEditPost } from '../utils/permissions'
import { ContentType } from '@insided/shared/utils/contentType'
import PostActions from '../content/post-actions.sync'
import { PageUnloadContext } from '@insided/shared/services/PageUnloadContext'

interface TopicReplyProps {
  type: ContentType
  currentUser: LikedUserType
  displayAuthor: boolean
  isLastItem: boolean
  topicId: number
  reply: PostType
  isPinned?: boolean
  topicTitle: string
}

interface TopicReplyState {
  showEditor: boolean
  isCurrentUserLiked: boolean
  likedUsers: {
    [key: string]: LikedUserType
  }
  content: string
}

class TopicReply extends Component<TopicReplyProps, TopicReplyState> {
  private readonly canUserEditPost: boolean = canUserEditPost()
  constructor (props: TopicReplyProps) {
    super(props)
    this.state = {
      showEditor: false,
      isCurrentUserLiked: this.props.currentUser.id !== null && this.props.reply.likedBy.includes(this.props.currentUser.id.toString()),
      likedUsers: {},
      content: props.reply.content
    }

    this.toggleLikeStatus = this.toggleLikeStatus.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onCancel = this.onCancel.bind(this)
  }

  content = createRef()
  // This content ref is responsible for storing the previous value of the post when we edit topic.
  // onChange method keeps updating the state and when we cancel the edit the content uses the value from state
  // which is changed. We can also set the value we receive from props but this is received from twig, and it is
  // not refreshed until we refresh the browser.

  async toggleLikeStatus () {
    if (this.state.isCurrentUserLiked) {
      delete this.state.likedUsers[this.props.currentUser.id]
      this.setState({
        likedUsers: this.state.likedUsers,
        isCurrentUserLiked: false
      })
    } else {
      this.setState({
        likedUsers: {
          ...this.state.likedUsers,
          [this.props.currentUser.id]: this.props.currentUser
        },
        isCurrentUserLiked: true
      })
    }
  }

  async componentDidMount () {
    if (this.props.reply.likedBy.length) {
      const res = await Axios.get('/member/getUsersById', {
        params: {
          ids: this.props.reply.likedBy
        }
      })

      this.setState({ likedUsers: res.data })
    }
    this.content.current = this.props.reply.content
  }

  onChange (content: string) {
    this.setState({ content })
  }

  onCancel () {
    this.setState({ showEditor: false, content: this.content.current })
  }

  render () {
    const { topicId, reply, displayAuthor, isLastItem, type, isPinned, topicTitle } = this.props
    const { content, showEditor, isCurrentUserLiked, likedUsers } = this.state
    const { content: data } = reply
    const showReport = common.isModuleActive('reportedContent')

    return (
      <div
        id={`post${reply.id}`} className={classNames(
          'post', 'box__pad', 'qa-topic-post-box',
          { 'post--pinned': isPinned }
        )}
      >
        <div className='post__user__info'>
          <PostAuthor author={reply.author} />
        </div>
        <PostAuthorMeta {...reply.author} displayAuthor={displayAuthor} repliedAt={reply.repliedAt} />
        {
          showEditor
            ? (
              <PageUnloadContext.Consumer>
                {({ lock, unlock }) => {
                  return (
                    <div className='post__content post__edit'>
                      <PostEditor
                        type={type}
                        data={content}
                        disableFileAttachments={false}
                        topicId={topicId}
                        replyId={parseInt(reply.id)}
                        isControl={false}
                        onSuccess={(content) => {
                          unlock()
                          this.content.current = content
                          this.setState({ showEditor: false, content })
                        }}
                        onCancel={() => {
                          unlock()
                          this.onCancel()
                        }}
                        redirect={false}
                        isGuest={false}
                        onChange={(content) => {
                          lock(content, data)
                          this.onChange(content)
                        }}
                      />
                    </div>
                  )
                }}
              </PageUnloadContext.Consumer>
              )
            : (
              <div>
                {isPinned && <div className='pinned-reply__label--comments'>
                  <PinnedReplyLabel phrase={<Phrase p='reply.pinned' m='Forum' />} />
                </div>}
                <Content
                  content={content || ''}
                  className={classNames('post__content', 'qa-topic-post-content', 'post__content--new-editor')}
                />
              </div>
              )
        }
        {
          Object.keys(likedUsers).length
            ? (
              <LikedUsers likedUsers={Object.keys(likedUsers).map(id => likedUsers[id])} previewCount={3} isCurrentUserLiked={isCurrentUserLiked} />
              )
            : null
        }
        <div className='post__footer'>
          <PostActions
            privateReplyId={parseInt(reply.id)}
            postId={parseInt(reply.publicReplyId)}
            contentType={type}
            onEditClick={() => this.setState({ showEditor: true })}
            isLiked={isCurrentUserLiked}
            onLikeClick={this.toggleLikeStatus}
            showLike
            showReport={showReport}
            showBestAnswer={false}
            quoteText={reply.content}
            quoteUsername={reply.author.username}
            showEdit={this.canUserEditPost}
            topicTitle={topicTitle}
          />
        </div>
        <hr className={`seperator m-b-0 ${isLastItem ? 'is-transparant' : ''}`} />
      </div>
    )
  }
}

export default TopicReply
